"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent } from "@/components/ui/card"
import { Slider } from "@/components/ui/slider"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Loader2, Download, Share2, BookmarkPlus } from "lucide-react"
import Image from "next/image"
import { useLanguage } from "@/contexts/language-context"
import { imageAPI } from "@/lib/api"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertCircle } from "lucide-react"

export default function CreatePage() {
  const { t } = useLanguage()
  const [prompt, setPrompt] = useState("")
  const [negativePrompt, setNegativePrompt] = useState("")
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedImage, setGeneratedImage] = useState<string | null>(null)
  const [selectedStyle, setSelectedStyle] = useState("none")
  const [selectedColorTone, setSelectedColorTone] = useState("none")
  const [selectedLighting, setSelectedLighting] = useState("none")
  const [selectedComposition, setSelectedComposition] = useState("none")
  const [error, setError] = useState<string | null>(null)
  const [polling, setPolling] = useState(false)

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError(t("promptRequired"))
      return
    }

    setIsGenerating(true)
    setGeneratedImage(null)
    setError(null)
    setPolling(false)

    try {
      // 调用图像生成API
      const result = await imageAPI.generate(prompt.trim(), {
        negativePrompt: negativePrompt.trim(),
        style: selectedStyle,
        colorTone: selectedColorTone,
        lighting: selectedLighting,
        composition: selectedComposition,
        highQuality: true, // 创建页面默认使用高质量
      })

      if (result.task_id) {
        // 轮询任务状态
        pollTaskStatus(result.task_id)
      } else if (result.image_url) {
        setGeneratedImage(result.image_url)
        setIsGenerating(false)
      }
    } catch (error) {
      console.error("图像生成失败:", error)
      setError(error instanceof Error ? error.message : t("generateFailed"))
      setIsGenerating(false)
    }
  }

  // 添加轮询函数
  const pollTaskStatus = async (taskId: string) => {
    if (polling) return // 防止并发轮询
    setPolling(true)
    try {
      const result = await imageAPI.getTaskStatus(taskId)
      if (result.status === "completed") {
        setGeneratedImage(result.image_url)
        setIsGenerating(false)
        setPolling(false)
      } else if (result.status === "failed") {
        setError(result.error || t("generateFailed"))
        setIsGenerating(false)
        setPolling(false)
      } else if (result.status === "processing") {
        setTimeout(() => pollTaskStatus(taskId), 2000)
      }
    } catch (error) {
      console.error("轮询任务状态失败:", error)
      setError(t("unknownError"))
      setIsGenerating(false)
      setPolling(false)
    }
  }

  const styleOptions = [
    { value: "none", label: t("noStyle") },
    { value: "digital-art", label: t("digitalArt") },
    { value: "cyberpunk", label: t("cyberpunk") },
    { value: "line-art", label: t("lineArt") },
    { value: "photography", label: t("photography") },
    { value: "film", label: t("film") },
    { value: "origami", label: t("origami") },
    { value: "3d-model", label: t("model3d") },
    { value: "anime", label: t("anime") },
    { value: "fantasy-art", label: t("fantasyArt") },
    { value: "low-poly", label: t("lowPoly") },
    { value: "cinematic", label: t("cinematic") },
    { value: "enhanced", label: t("enhanced") },
    { value: "comic-book", label: t("comicBook") },
    { value: "isometric", label: t("isometric") },
    { value: "clay", label: t("clay") },
  ]

  const colorToneOptions = [
    { value: "none", label: t("noColor") },
    { value: "warm-tones", label: t("warmTones") },
    { value: "cool-tones", label: t("coolTones") },
    { value: "soft-colors", label: t("softColors") },
    { value: "vibrant-colors", label: t("vibrantColors") },
    { value: "pastel-colors", label: t("pastelColors") },
    { value: "black-white", label: t("blackAndWhite") },
  ]

  const lightingOptions = [
    { value: "none", label: t("noLighting") },
    { value: "dramatic", label: t("dramatic") },
    { value: "dim-lighting", label: t("dimLighting") },
    { value: "studio", label: t("studio") },
    { value: "low-light", label: t("lowLight") },
    { value: "golden-hour", label: t("goldenHour") },
    { value: "backlight", label: t("backlight") },
    { value: "volumetric-light", label: t("volumetricLight") },
    { value: "tyndall-effect", label: t("tyndallEffect") },
    { value: "sunlight", label: t("sunlight") },
    { value: "rim-light", label: t("rimLight") },
  ]

  const compositionOptions = [
    { value: "none", label: t("noComposition") },
    { value: "blurred-background", label: t("blurredBackground") },
    { value: "close-up", label: t("closeUp") },
    { value: "wide-angle", label: t("wideAngle") },
    { value: "shallow-depth", label: t("shallowDepthOfField") },
    { value: "bottom-up", label: t("bottomUpShot") },
    { value: "top-down", label: t("topDownShot") },
    { value: "macro", label: t("macroPhotography") },
  ]

  return (
    <div className="container mx-auto px-4 py-12 md:px-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="flex flex-col space-y-4">
          <h1 className="text-3xl font-bold tracking-tight">{t("createImage")}</h1>
          <p className="text-muted-foreground">{t("createDesc")}</p>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prompt">{t("prompt")}</Label>
              <Textarea
                id="prompt"
                placeholder={t("promptPlaceholder")}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[120px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="negative-prompt">{t("negativePrompt")}</Label>
              <Textarea
                id="negative-prompt"
                placeholder={t("negativePromptPlaceholder")}
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                className="min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="style">{t("style")}</Label>
                <Select value={selectedStyle} onValueChange={setSelectedStyle}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("style")} />
                  </SelectTrigger>
                  <SelectContent>
                    {styleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="color-tone">{t("colorTone")}</Label>
                <Select value={selectedColorTone} onValueChange={setSelectedColorTone}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("colorTone")} />
                  </SelectTrigger>
                  <SelectContent>
                    {colorToneOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lighting">{t("lighting")}</Label>
                <Select value={selectedLighting} onValueChange={setSelectedLighting}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("lighting")} />
                  </SelectTrigger>
                  <SelectContent>
                    {lightingOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="composition">{t("composition")}</Label>
                <Select value={selectedComposition} onValueChange={setSelectedComposition}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("composition")} />
                  </SelectTrigger>
                  <SelectContent>
                    {compositionOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="guidance-scale">{t("promptStrength")}</Label>
                <span className="text-sm text-muted-foreground">7.5</span>
              </div>
              <Slider id="guidance-scale" defaultValue={[7.5]} max={15} min={1} step={0.1} />
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="high-quality" />
              <Label htmlFor="high-quality">{t("highQualityGeneration")}</Label>
            </div>
            <Button
              onClick={handleGenerate}
              disabled={!prompt || isGenerating}
              className="w-full bg-amber-500 hover:bg-amber-600"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("generating")}
                </>
              ) : (
                t("generateImage")
              )}
            </Button>
          </div>
        </div>

        <div className="flex flex-col space-y-4">
          <h2 className="text-xl font-bold">{t("preview")}</h2>
          {/* 错误提示 */}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              {isGenerating ? (
                <div className="flex items-center justify-center bg-muted/20 aspect-square">
                  <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
                </div>
              ) : generatedImage ? (
                <div className="relative aspect-square">
                  <Image
                    src={generatedImage || "/placeholder.svg"}
                    alt={t("generateImage")}
                    fill
                    className="object-cover"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center bg-muted/20 aspect-square p-6 text-center">
                  <p className="text-muted-foreground">{t("imageWillAppear")}</p>
                  <p className="text-sm text-muted-foreground mt-2">{t("enterPrompt")}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {generatedImage && (
            <div className="flex space-x-2">
              <Button variant="outline" className="flex-1">
                <Download className="mr-2 h-4 w-4" />
                {t("download")}
              </Button>
              <Button variant="outline" className="flex-1">
                <Share2 className="mr-2 h-4 w-4" />
                {t("share")}
              </Button>
              <Button variant="outline" className="flex-1">
                <BookmarkPlus className="mr-2 h-4 w-4" />
                {t("bookmark")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
